import {
  Badge,
  Button,
  Card,
  createTheme,
  type CSSVariablesResolver,
  Input,
  Modal,
  Paper,
  Table,
  Tabs,
} from '@mantine/core';
import { DateInput, DatePicker, DatePickerInput } from '@mantine/dates';

import { parseFrenchDate } from '@/lib/dates';

import {
  ACCENTS,
  type AccentKey,
  accentPalette,
  type Appearance,
  DEFAULT_APPEARANCE,
  headingFontStack,
  uiFontStack,
} from './appearance/accents';

// Noms accessibles des flèches et niveaux du calendrier (lecteurs d'écran, tests).
const calendarAriaLabels = {
  previousMonth: 'Mois précédent',
  nextMonth: 'Mois suivant',
  previousYear: 'Année précédente',
  nextYear: 'Année suivante',
  previousDecade: 'Décennie précédente',
  nextDecade: 'Décennie suivante',
  monthLevelControl: 'Choisir le mois',
  yearLevelControl: 'Choisir l’année',
};

const accentColors = Object.fromEntries(
  (Object.keys(ACCENTS) as AccentKey[]).map((key) => [key, accentPalette(key)]),
);

/**
 * Thème de l'application, calqué sur « Gestion Bulletins » : fond vert d'eau,
 * cartes blanches bordées, titres en Bricolage Grotesque, texte en Figtree.
 */
export function buildTheme(appearance: Appearance = DEFAULT_APPEARANCE) {
  return createTheme({
    colors: accentColors,
    primaryColor: appearance.accent,
    primaryShade: { light: 6, dark: 4 },
    autoContrast: true,
    defaultRadius: 'md',
    fontFamily: uiFontStack(appearance.font),
    fontFamilyMonospace: 'ui-monospace, Consolas, monospace',
    headings: { fontFamily: headingFontStack(appearance), fontWeight: '800' },
    components: {
      // Toutes les dates se saisissent et s'affichent en JJ/MM/AAAA.
      DateInput: DateInput.extend({
        defaultProps: {
          valueFormat: 'DD/MM/YYYY',
          dateParser: parseFrenchDate,
          ariaLabels: calendarAriaLabels,
        },
      }),
      DatePickerInput: DatePickerInput.extend({
        defaultProps: { valueFormat: 'DD/MM/YYYY', ariaLabels: calendarAriaLabels },
      }),
      DatePicker: DatePicker.extend({ defaultProps: { ariaLabels: calendarAriaLabels } }),
      Card: Card.extend({ defaultProps: { radius: 12, padding: 'md' } }),
      Paper: Paper.extend({ defaultProps: { radius: 12 } }),
      Button: Button.extend({ defaultProps: { radius: 8 } }),
      Input: Input.extend({ defaultProps: { radius: 8 } }),
      Badge: Badge.extend({ defaultProps: { radius: 'xl' } }),
      Tabs: Tabs.extend({ defaultProps: { color: appearance.accent } }),
      Table: Table.extend({ defaultProps: { verticalSpacing: 6, horizontalSpacing: 10 } }),
      Modal: Modal.extend({
        defaultProps: {
          radius: 16,
          overlayProps: { color: '#0a1412', backgroundOpacity: 0.55, blur: 3 },
          // Comme une boîte de dialogue Windows : un clic à côté ne ferme pas la fenêtre
          // (il déclenche un signal sonore), pour ne pas perdre une saisie en cours.
          closeOnClickOutside: false,
        },
      }),
    },
  });
}

/** Thème par défaut (tests unitaires, écrans sans apparence chargée). */
export const theme = buildTheme();

/**
 * Variables Mantine alignées sur les jetons de couleur de « Gestion Bulletins »
 * (--bg, --surface, --ink, --muted, --line) définis dans gb-theme.css.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => {
  const shared = {
    '--mantine-color-body': 'var(--bg)',
    '--mantine-color-text': 'var(--ink)',
    '--mantine-color-dimmed': 'var(--muted)',
    '--mantine-color-default': 'var(--surface)',
    '--mantine-color-default-hover': 'var(--surface2)',
    '--mantine-color-default-color': 'var(--ink)',
    '--mantine-color-default-border': 'var(--line)',
    '--mantine-color-placeholder': 'var(--muted)',
    '--mantine-color-anchor': 'var(--accent)',
  };
  return { variables: {}, light: shared, dark: shared };
};
